import React from 'react';
import {EntityRefOfRequirementInput, WorkflowStatus} from 'types';
import * as Apollo from '@apollo/client';
import {
	useUpdateRegulatoryDocumentWorkflowStatusMutation,
	useUpdateGetexRegulationWorkflowStatusMutation,
} from '../hooks/useUpdateRegulatoryDocumentWorkflowStatus.generated';
import {useAssignGetexRegulationParagraphsRequirementsMutation} from '../hooks/useAssignGetexRegulationParagraphsRequirements.generated';
import {useAssignRegulatoryDocumentParagraphsRequirementsMutation} from '../hooks/useAssignRegulatoryDocumentParagraphsRequirement.generated';
import {useUpdateGetexVexClusterAssignmentBatchMutation} from '../hooks/useUpdateGetexVexClusterAssignment.generated';
import {useUpdateVexClusterAssignmentBatchMutation} from '../hooks/useUpdateVexClusterAssignment.generated';
import {GetWorkbenchItemsVexDocument} from 'features/Dashboard/hooks/workbenchItems.generated';
import {GetVexRegulatoryDocumentParagraphsDocument} from '../hooks/useGetRegulatoryDocumentDetails.generated';
import {RegDocInjectedDataContext} from './RegDocInjectedDataContext';

const vexClusterRefetchQueries = [
	GetWorkbenchItemsVexDocument,
	GetVexRegulatoryDocumentParagraphsDocument,
];

export const useGetexMutations = () => {
	const isGetexInjected = React.useContext(RegDocInjectedDataContext) !== null;
	const [updateRegulatoryDocumentWorkflowStatus] =
		useUpdateRegulatoryDocumentWorkflowStatusMutation();
	const [updateGetexRegulationWorkflowStatus] =
		useUpdateGetexRegulationWorkflowStatusMutation();
	const [assignGetexRegulationParagraphsRequirements] =
		useAssignGetexRegulationParagraphsRequirementsMutation();
	const [assignRegulatoryDocumentParagraphsRequirements] =
		useAssignRegulatoryDocumentParagraphsRequirementsMutation();
	const [updateGetexVexClusterAssignmentBatch] =
		useUpdateGetexVexClusterAssignmentBatchMutation();
	const [updateVexClusterAssignmentBatch] =
		useUpdateVexClusterAssignmentBatchMutation();

	const updateWorkflowStatus = (
		regulatoryDocumentId: string,
		workflowStatus: WorkflowStatus,
	) =>
		isGetexInjected
			? updateGetexRegulationWorkflowStatus({
					variables: {
						getexRegulationId: regulatoryDocumentId,
						workflowStatus,
					},
			  })
			: updateRegulatoryDocumentWorkflowStatus({
					variables: {
						regulatoryDocumentId,
						workflowStatus,
					},
			  });

	const assignParagraphsRequirements = (
		documentId: string,
		requirementRefs: EntityRefOfRequirementInput[],
		paragraphIds: string[],
		refetchQueries?: Apollo.MutationOptions['refetchQueries'],
	) =>
		isGetexInjected
			? assignGetexRegulationParagraphsRequirements({
					variables: {
						input: {
							getexRegulationId: documentId,
							requirementRefs,
							paragraphIds,
						},
					},
					refetchQueries,
			  })
			: assignRegulatoryDocumentParagraphsRequirements({
					variables: {
						input: {
							regulatoryDocumentId: documentId,
							requirementRefs,
							paragraphIds,
						},
					},
					refetchQueries,
			  });

	const submitVexClusterAssignment = (baseInput: any, documentId: string) =>
		isGetexInjected
			? updateGetexVexClusterAssignmentBatch({
					variables: {
						input: {...baseInput, getexRegulationId: documentId},
					},
					refetchQueries: vexClusterRefetchQueries,
			  })
			: updateVexClusterAssignmentBatch({
					variables: {
						input: {...baseInput, regulatoryDocumentId: documentId},
					},
					refetchQueries: vexClusterRefetchQueries,
			  });

	return {
		updateWorkflowStatus,
		assignParagraphsRequirements,
		submitVexClusterAssignment,
	};
};
